import { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Card, Row, Col, Form, Button, Table, Spinner, Alert } from 'react-bootstrap';
import { FaSave, FaUserSecret } from 'react-icons/fa';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useTranslation, withTranslation } from 'react-i18next';
import privacidadeService, {
    CATEGORIAS_PRESTADOR,
    DIAS_ANONIMIZACAO,
    GRUPOS_VISIBILIDADE,
    REGRAS_ANONIMIZACAO,
} from '../../api/programas/privacidadeService';
import { formatarDataHora } from '../../i18n/format';

const MySwal = withReactContent(Swal);

const campoDias = (regra) => `anonimizar_${regra}_dias`;

/** Achata a mensagem de erro do DRF (lista, objeto aninhado ou texto) em uma string. */
const textoDoErro = (valor) => {
    if (Array.isArray(valor)) {
        return valor.map(textoDoErro).join(' ');
    }

    if (valor && typeof valor === 'object') {
        return Object.values(valor).map(textoDoErro).join(' ');
    }

    return String(valor ?? '');
};

/** Título da aba: o ProgramasEdit não é traduzido, então o texto vem deste módulo. */
export function ProgramaPrivacidadeTitulo() {
    const { t } = useTranslation('privacidade');
    return t('privacidade:tab.title');
}

export function ProgramaPrivacidadeCarregar() {
    const { t } = useTranslation('privacidade');
    return t('privacidade:tab.clickToLoad');
}

class ProgramaPrivacidade extends Component {
    constructor(props) {
        super(props);

        const regras = {};
        REGRAS_ANONIMIZACAO.forEach((regra) => {
            regras[regra] = { ativa: false, dias: '' };
        });

        this.state = {
            loading: true,
            erroCarregamento: null,
            salvando: false,

            regras,
            anonimizar_farmacovigilancia: false,
            versaoVisibilidade: 1,
            bloqueios: {},

            atualizado_por: '',
            updated_at: null,

            // { campo: { tipo: 'obrigatorio' | 'intervalo' } | { texto } }
            errosCampos: {},
        };
    }

    componentDidMount() {
        this.carregar();
    }

    carregar = async () => {
        const { programaId } = this.props;

        this.setState({ loading: true, erroCarregamento: null });

        try {
            const { data } = await privacidadeService.get(programaId);
            this.aplicarDados(data);
        } catch (error) {
            this.setState({
                loading: false,
                erroCarregamento: error?.response?.status === 403 ? 'sem_permissao' : 'falha',
            });
        }
    };

    aplicarDados = (data) => {
        const regras = {};
        REGRAS_ANONIMIZACAO.forEach((regra) => {
            const dias = data?.[campoDias(regra)];
            regras[regra] = {
                ativa: dias !== null && dias !== undefined,
                dias: dias ?? '',
            };
        });

        const visibilidade = data?.visibilidade_paciente || {};

        this.setState({
            loading: false,
            regras,
            anonimizar_farmacovigilancia: !!data?.anonimizar_farmacovigilancia,
            versaoVisibilidade: visibilidade.versao || 1,
            bloqueios: visibilidade.bloqueios || {},
            // etapas que continuam visíveis para quem tem o histórico bloqueado; a tela não edita, só preserva
            historicoCompartilhado: visibilidade.historico_compartilhado || {},
            atualizado_por: data?.atualizado_por || '',
            updated_at: data?.updated_at || null,
            errosCampos: {},
        });
    };

    rotuloCategoria = (categoria) => {
        const { t, i18n } = this.props;
        const chaveEnum = `enums:categoriaPrestador.${categoria}`;

        if (i18n.exists(chaveEnum)) {
            return t(chaveEnum);
        }

        return t(`privacidade:categoryFallback.${categoria}`, { defaultValue: categoria });
    };

    handleRegraAtiva = (regra, ativa) => {
        this.setState((prev) => {
            const errosCampos = { ...prev.errosCampos };
            delete errosCampos[campoDias(regra)];

            return {
                regras: { ...prev.regras, [regra]: { ...prev.regras[regra], ativa } },
                errosCampos,
            };
        });
    };

    handleRegraDias = (regra, dias) => {
        this.setState((prev) => {
            const errosCampos = { ...prev.errosCampos };
            delete errosCampos[campoDias(regra)];

            return {
                regras: { ...prev.regras, [regra]: { ...prev.regras[regra], dias } },
                errosCampos,
            };
        });
    };

    handleBloqueio = (categoria, grupo, oculto) => {
        this.setState((prev) => {
            const atuais = prev.bloqueios[categoria] || [];
            // Mantém a ordem canônica dos grupos.
            const novos = oculto
                ? GRUPOS_VISIBILIDADE.filter((g) => g === grupo || atuais.includes(g))
                : atuais.filter((g) => g !== grupo);

            // Categorias fora da lista conhecida são preservadas como vieram.
            const bloqueios = { ...prev.bloqueios };

            if (novos.length) {
                bloqueios[categoria] = novos;
            } else {
                delete bloqueios[categoria];
            }

            return { bloqueios };
        });
    };

    validar = () => {
        const errosCampos = {};

        REGRAS_ANONIMIZACAO.forEach((regra) => {
            const { ativa, dias } = this.state.regras[regra];

            if (!ativa) {
                return;
            }

            if (dias === '' || dias === null) {
                errosCampos[campoDias(regra)] = { tipo: 'obrigatorio' };
                return;
            }

            const numero = Number(dias);

            if (!Number.isInteger(numero) || numero < DIAS_ANONIMIZACAO.min || numero > DIAS_ANONIMIZACAO.max) {
                errosCampos[campoDias(regra)] = { tipo: 'intervalo' };
            }
        });

        return errosCampos;
    };

    montarPayload = () => {
        const { regras, anonimizar_farmacovigilancia, versaoVisibilidade, bloqueios, historicoCompartilhado } = this.state;
        const payload = { anonimizar_farmacovigilancia };

        REGRAS_ANONIMIZACAO.forEach((regra) => {
            payload[campoDias(regra)] = regras[regra].ativa ? Number(regras[regra].dias) : null;
        });

        payload.visibilidade_paciente = { versao: versaoVisibilidade, bloqueios };

        if (historicoCompartilhado && Object.keys(historicoCompartilhado).length) {
            payload.visibilidade_paciente.historico_compartilhado = historicoCompartilhado;
        }

        return payload;
    };

    salvar = async () => {
        const { t, programaId } = this.props;
        const errosLocais = this.validar();

        if (Object.keys(errosLocais).length) {
            this.setState({ errosCampos: errosLocais });
            return;
        }

        this.setState({ salvando: true, errosCampos: {} });

        try {
            const { data } = await privacidadeService.update(programaId, this.montarPayload());
            this.aplicarDados(data);
            this.setState({ salvando: false });

            MySwal.fire({
                icon: 'success',
                title: t('common:alert.success'),
                text: t('privacidade:config.saved'),
            });
        } catch (error) {
            const status = error?.response?.status;
            const dados = error?.response?.data;

            if (status === 400 && dados && typeof dados === 'object') {
                const camposConhecidos = [
                    ...REGRAS_ANONIMIZACAO.map(campoDias),
                    'anonimizar_farmacovigilancia',
                    'visibilidade_paciente',
                ];
                const errosCampos = {};
                const gerais = [];

                Object.entries(dados).forEach(([campo, valor]) => {
                    if (camposConhecidos.includes(campo)) {
                        errosCampos[campo] = { texto: textoDoErro(valor) };
                    } else {
                        gerais.push(textoDoErro(valor));
                    }
                });

                this.setState({ salvando: false, errosCampos });

                MySwal.fire({
                    icon: 'error',
                    title: t('common:alert.error'),
                    text: gerais.length ? gerais.join(' ') : t('privacidade:config.fixErrors'),
                });
                return;
            }

            this.setState({ salvando: false });

            let mensagem = t('privacidade:config.saveError');

            if (dados?.detail) {
                mensagem = dados.detail;
            } else if (status === 403) {
                mensagem = t('privacidade:errors.forbidden');
            }

            MySwal.fire({ icon: 'error', title: t('common:alert.error'), text: mensagem });
        }
    };

    renderErroCampo = (erro) => {
        const { t } = this.props;

        if (!erro) {
            return null;
        }

        if (erro.tipo === 'obrigatorio') {
            return t('privacidade:config.rule.required');
        }

        if (erro.tipo === 'intervalo') {
            return t('privacidade:config.rule.range', DIAS_ANONIMIZACAO);
        }

        return erro.texto;
    };

    renderRegras() {
        const { t, programaId } = this.props;
        const { regras, anonimizar_farmacovigilancia, errosCampos } = this.state;

        return (
            <Card className="cardDestaque mb-3">
                <Card.Body>
                    <h5>{t('privacidade:config.rulesCard.title')}</h5>
                    <p className="text-muted mb-2">{t('privacidade:config.rulesCard.description')}</p>
                    <Alert variant="warning" className="small">
                        {t('privacidade:config.rulesCard.manualIrreversible')}
                    </Alert>

                    <Row>
                        {REGRAS_ANONIMIZACAO.map((regra) => {
                            const campo = campoDias(regra);
                            const erro = errosCampos[campo];

                            return (
                                <Col md={4} xs={12} key={regra} className="mb-3">
                                    <div className="border rounded p-3 h-100">
                                        <h6 className="mb-1">{t(`privacidade:ruleCode.${regra}`)}</h6>
                                        <Form.Text className="d-block mb-2">{t(`privacidade:ruleHelp.${regra}`)}</Form.Text>

                                        <Form.Check
                                            type="switch"
                                            id={`privacidade-regra-${regra}`}
                                            className="mb-2"
                                            label={t('privacidade:config.rule.active')}
                                            checked={regras[regra].ativa}
                                            onChange={(e) => this.handleRegraAtiva(regra, e.target.checked)}
                                        />

                                        <Form.Group controlId={`privacidade-dias-${regra}`}>
                                            <Form.Label className="small mb-1">{t('privacidade:config.rule.days')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                min={DIAS_ANONIMIZACAO.min}
                                                max={DIAS_ANONIMIZACAO.max}
                                                step={1}
                                                value={regras[regra].dias}
                                                disabled={!regras[regra].ativa}
                                                isInvalid={!!erro}
                                                onChange={(e) => this.handleRegraDias(regra, e.target.value)}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {this.renderErroCampo(erro)}
                                            </Form.Control.Feedback>
                                            {!erro && (
                                                <Form.Text>{t('privacidade:config.rule.daysHelp', DIAS_ANONIMIZACAO)}</Form.Text>
                                            )}
                                        </Form.Group>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            id="privacidade-farmacovigilancia"
                            label={t('privacidade:config.pharmacovigilance.label')}
                            checked={anonimizar_farmacovigilancia}
                            isInvalid={!!errosCampos.anonimizar_farmacovigilancia}
                            onChange={(e) => this.setState({ anonimizar_farmacovigilancia: e.target.checked })}
                        />
                        <Form.Text className="d-block">{t('privacidade:config.pharmacovigilance.help')}</Form.Text>
                        {errosCampos.anonimizar_farmacovigilancia && (
                            <div className="text-danger small">{this.renderErroCampo(errosCampos.anonimizar_farmacovigilancia)}</div>
                        )}
                    </Form.Group>

                    <Button
                        as={Link}
                        to={`/suporte/anonimizacao?programa_id=${programaId}`}
                        variant="outline-danger"
                        size="sm"
                    >
                        <FaUserSecret className="me-1" /> {t('privacidade:config.rulesCard.goToExecution')}
                    </Button>
                </Card.Body>
            </Card>
        );
    }

    renderVisibilidade() {
        const { t } = this.props;
        const { bloqueios, errosCampos } = this.state;

        return (
            <Card className="cardDestaque mb-3">
                <Card.Body>
                    <h5>{t('privacidade:config.visibilityCard.title')}</h5>
                    <p className="text-muted">{t('privacidade:config.visibilityCard.description')}</p>

                    {errosCampos.visibilidade_paciente && (
                        <Alert variant="danger" className="small">
                            {this.renderErroCampo(errosCampos.visibilidade_paciente)}
                        </Alert>
                    )}

                    {/* table-interna compensa o recuo que .table-responsive aplica no CSS global */}
                    <div className="table-responsive table-interna">
                    <Table size="sm" hover bordered className="align-middle">
                        <thead>
                            <tr>
                                <th>{t('privacidade:config.visibilityCard.category')}</th>
                                {GRUPOS_VISIBILIDADE.map((grupo) => (
                                    <th key={grupo} className="text-center text-nowrap">
                                        {t(`privacidade:group.${grupo}`)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {CATEGORIAS_PRESTADOR.map((categoria) => {
                                const rotulo = this.rotuloCategoria(categoria);
                                const ocultos = bloqueios[categoria] || [];

                                return (
                                    <tr key={categoria}>
                                        <td className="text-nowrap">{rotulo}</td>
                                        {GRUPOS_VISIBILIDADE.map((grupo) => (
                                            <td key={grupo} className="text-center">
                                                <Form.Check
                                                    type="checkbox"
                                                    className="d-inline-block"
                                                    id={`privacidade-${categoria}-${grupo}`}
                                                    aria-label={t('privacidade:config.visibilityCard.hiddenFor', {
                                                        grupo: t(`privacidade:group.${grupo}`),
                                                        categoria: rotulo,
                                                    })}
                                                    checked={ocultos.includes(grupo)}
                                                    onChange={(e) => this.handleBloqueio(categoria, grupo, e.target.checked)}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                    </div>

                    <div className="small text-muted">
                        <strong>{t('privacidade:config.visibilityCard.legendTitle')}</strong>
                        <ul className="mb-0 mt-1">
                            <li>{t('privacidade:config.visibilityCard.legend.nome')}</li>
                            <li>{t('privacidade:config.visibilityCard.legend.cpf')}</li>
                            <li>{t('privacidade:config.visibilityCard.legend.endereco')}</li>
                            <li>{t('privacidade:config.visibilityCard.legend.contato')}</li>
                            <li>{t('privacidade:config.visibilityCard.legend.termo')}</li>
                            <li>{t('privacidade:config.visibilityCard.legend.historico')}</li>
                        </ul>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    render() {
        const { t } = this.props;
        const { loading, erroCarregamento, salvando, atualizado_por, updated_at } = this.state;

        if (loading) {
            return (
                <Card className="cardDestaque">
                    <Card.Body className="text-center py-4">
                        <Spinner />
                    </Card.Body>
                </Card>
            );
        }

        if (erroCarregamento) {
            return (
                <Card className="cardDestaque">
                    <Card.Body>
                        <Alert variant="danger">
                            {erroCarregamento === 'sem_permissao'
                                ? t('privacidade:errors.forbidden')
                                : t('privacidade:config.loadError')}
                        </Alert>
                        {erroCarregamento !== 'sem_permissao' && (
                            <Button variant="secondary" size="sm" onClick={this.carregar}>
                                {t('privacidade:config.retry')}
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            );
        }

        return (
            <>
                {this.renderRegras()}
                {this.renderVisibilidade()}

                <Row className="align-items-center mb-3">
                    <Col md={8} xs={12} className="text-muted small mb-2 mb-md-0">
                        {atualizado_por && updated_at && t('privacidade:config.lastUpdate', {
                            usuario: atualizado_por,
                            data: formatarDataHora(updated_at),
                        })}
                    </Col>
                    <Col md={4} xs={12} className="text-md-end">
                        <Button variant="primary" onClick={this.salvar} disabled={salvando}>
                            {salvando ? <Spinner size="sm" className="me-1" /> : <FaSave className="me-1" />}
                            {t('privacidade:config.save')}
                        </Button>
                    </Col>
                </Row>
            </>
        );
    }
}

ProgramaPrivacidade.propTypes = {
    t: PropTypes.func.isRequired,
    i18n: PropTypes.object.isRequired,
    programaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

const ProgramaPrivacidadeTraduzida = withTranslation(['privacidade', 'common', 'enums'])(ProgramaPrivacidade);

export default ProgramaPrivacidadeTraduzida;
